/**
 * Calendar dates in the organisation's time zone (America/New_York, REQ-029).
 *
 * A week runs Sunday to Saturday in Eastern time, and the server decides
 * "today" the same way (`getTodayET()`). The browser used to take local Sunday
 * at the current time of day and call `toISOString()`, which is UTC: after
 * 8 PM EDT (7 PM EST) the "current week" became that week's Monday.
 *
 * Dates here are plain `YYYY-MM-DD` strings. Arithmetic runs on UTC midnights
 * only, so no daylight-saving change can shift a day.
 */

const EASTERN_TIME_ZONE = 'America/New_York';

const easternDateParts = new Intl.DateTimeFormat('en-CA', {
  timeZone: EASTERN_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Today's date in Eastern time, e.g. "2026-10-18". */
export function todayET(now: Date = new Date()): string {
  const parts = easternDateParts.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** UTC midnight of a `YYYY-MM-DD` date. */
function utcMidnight(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
}

/** `isoDate` moved by `days` (negative for earlier). */
export function addDaysIso(isoDate: string, days: number): string {
  const date = utcMidnight(isoDate);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** The Sunday that starts the week holding `isoDate`. */
export function weekStartOf(isoDate: string): string {
  return addDaysIso(isoDate, -utcMidnight(isoDate).getUTCDay());
}

/** The Sunday that starts the current week in Eastern time. */
export function currentWeekStartET(now: Date = new Date()): string {
  return weekStartOf(todayET(now));
}
